<template>
  <div class="lifecycle-pipeline">
    <div class="pipeline-track">
      <div
        v-for="(stage, idx) in stages"
        :key="stage.key"
        class="pipeline-stage"
        :class="{ clickable: stage.tab }"
        @click="stage.tab && $emit('select-tab', stage.tab)"
      >
        <!-- 연결선 (첫 번째 제외) -->
        <div v-if="idx > 0" class="connector" :class="getConnectorClass(idx)" />

        <!-- 원형 아이콘 (호버 시 상태 근거 표시) -->
        <div class="stage-circle" :class="getStageClass(stage.key)" :title="stageTooltip(stage)">
          <span class="stage-icon">{{ stage.icon }}</span>
        </div>

        <!-- 라벨 -->
        <div class="stage-label" :class="getStageClass(stage.key)">
          {{ stage.label }}
        </div>

        <!-- 카운트 뱃지 -->
        <div v-if="getCount(stage.key)" class="stage-count">
          {{ getCount(stage.key) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  lifecycle: { type: Object, default: () => ({}) },
  materialOrderCount: { type: Number, default: 0 },
  tradeDataExists: { type: Boolean, default: false },
})

defineEmits(['select-tab'])

/**
 * 9단계 파이프라인 — 의류 에이전트 해외생산 업무 흐름
 *
 * 기획 → 샘플 → 견적 → 작업지시 → 자재조달 → 자재수출 → 생산 → 제품수입 → 납품
 *
 * - 자재조달: 원부자재 발주 + 한국 입고
 * - 자재수출: 한국 → 해외공장으로 자재 선적 (LC/B/L)
 * - 생산: 해외공장 생산 + 검품
 * - 제품수입: 완제품 선적 + 통관 + 한국 입고
 */
const stages = [
  { key: 'design', label: '기획', icon: '📋', tab: 'info' },
  { key: 'sample', label: '샘플', icon: '✂️', tab: 'samples' },
  { key: 'quotation', label: '견적', icon: '💰', tab: 'quotations' },
  { key: 'work_order', label: '작업지시', icon: '📝', tab: 'production' },
  { key: 'material_procurement', label: '자재조달', icon: '📦', tab: 'materials' },
  { key: 'material_export', label: '자재수출', icon: '🚢', tab: 'trade' },
  { key: 'production', label: '생산', icon: '🏭', tab: 'production' },
  { key: 'product_import', label: '제품수입', icon: '📥', tab: 'trade' },
  { key: 'delivery', label: '납품', icon: '✅', tab: 'production' },
]

// 9단계 상태를 lifecycle API로부터 매핑
const stageStatus = computed(() => {
  const lc = props.lifecycle
  if (!lc || !lc.design) return {}

  const productionStatus = lc.production?.status || 'none'
  const deliveryStatus = lc.delivery?.status || 'none'
  const hasWorkOrder = productionStatus !== 'none'
  const hasMaterialOrder = props.materialOrderCount > 0

  return {
    // 기획: 디자인 등록 완료
    design: lc.design?.status || 'completed',
    // 샘플: 샘플 요청/작업 상태
    sample: lc.sample?.status || 'none',
    // 견적: 견적서 작성/확정 상태
    quotation: lc.quotation?.status || 'none',
    // 작업지시: 메인오더가 존재하면 완료
    work_order: hasWorkOrder ? 'completed' : 'none',
    // 자재조달: 발주서 존재 여부 → 생산 시작 시 완료
    material_procurement: hasMaterialOrder
      ? (['in_progress', 'completed'].includes(productionStatus) ? 'completed' : 'in_progress')
      : (hasWorkOrder ? 'pending' : 'none'),
    // 자재수출: 무역데이터 존재 + 아직 생산 전이면 진행중, 생산 이후면 완료
    material_export: props.tradeDataExists
      ? (['in_progress', 'completed'].includes(productionStatus) ? 'completed' : 'in_progress')
      : (hasMaterialOrder ? 'pending' : 'none'),
    // 생산: 작업지시 상태 기반 (IN_PRODUCTION 이상)
    production: productionStatus,
    // 제품수입: 납품 완료면 완료, 선적중이면 진행중
    product_import: deliveryStatus === 'completed' ? 'completed'
      : deliveryStatus === 'in_progress' ? 'in_progress'
      : (productionStatus === 'completed' ? 'pending' : 'none'),
    // 납품: 최종 납품 상태
    delivery: deliveryStatus,
  }
})

const getStageClass = (key) => {
  const s = stageStatus.value[key]
  if (s === 'completed') return 'completed'
  if (s === 'in_progress') return 'active'
  if (s === 'pending') return 'pending'
  return 'pending'
}

// 단계 호버 툴팁 — "왜 이 색인지" 근거 (상태 추론이 복잡한 단계 설명)
const STATUS_LABEL = { completed: '완료', in_progress: '진행중', pending: '대기', none: '미시작' }
const STAGE_BASIS = {
  material_procurement: '원부자재 발주·입고 기준',
  material_export: '자재 선적(LC·B/L) 기준',
  product_import: '완제품 선적·통관 기준',
  work_order: '메인오더(메인요청) 존재 기준',
}
const stageTooltip = (stage) => {
  const s = stageStatus.value[stage.key] || 'none'
  const label = STATUS_LABEL[s] || s
  const basis = STAGE_BASIS[stage.key]
  return basis ? `${stage.label}: ${label} (${basis})` : `${stage.label}: ${label}`
}

const getConnectorClass = (idx) => {
  const prevKey = stages[idx - 1].key
  const prevStatus = stageStatus.value[prevKey]
  if (prevStatus === 'completed') return 'completed'
  if (prevStatus === 'in_progress') return 'active'
  return 'pending'
}

const getCount = (key) => {
  const lc = props.lifecycle
  if (!lc) return null
  if (key === 'sample') return lc.sample?.count || null
  if (key === 'quotation') return lc.quotation?.count || null
  if (key === 'work_order') return lc.production?.count || null
  if (key === 'material_procurement') return props.materialOrderCount || null
  return null
}
</script>

<style scoped>
.lifecycle-pipeline {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px 12px 16px;
  margin-bottom: 20px;
}

.pipeline-track {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
}

.pipeline-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 0;
}

.pipeline-stage.clickable {
  cursor: pointer;
}

.pipeline-stage.clickable:hover .stage-circle {
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* 연결선 */
.connector {
  position: absolute;
  top: 16px;
  right: 50%;
  width: 100%;
  height: 3px;
  background: #dcdfe6;
  z-index: 0;
}

.connector.completed {
  background: #67C23A;
}

.connector.active {
  background: linear-gradient(90deg, #67C23A, #409EFF);
}

/* 원형 아이콘 */
.stage-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e4e7ed;
  border: 3px solid #dcdfe6;
  z-index: 1;
  transition: all 0.2s;
  position: relative;
}

.stage-circle.completed {
  background: #f0f9eb;
  border-color: #67C23A;
}

.stage-circle.active {
  background: #ecf5ff;
  border-color: #409EFF;
  animation: pulse 2s infinite;
}

.stage-icon {
  font-size: 14px;
  line-height: 1;
}

/* 라벨 */
.stage-label {
  margin-top: 5px;
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
  font-weight: 500;
}

.stage-label.completed {
  color: #67C23A;
  font-weight: 600;
}

.stage-label.active {
  color: #409EFF;
  font-weight: 600;
}

/* 카운트 뱃지 */
.stage-count {
  position: absolute;
  top: -4px;
  right: calc(50% - 24px);
  background: #409EFF;
  color: white;
  font-size: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(64, 158, 255, 0); }
}
</style>
